/**
 * Display Units Hook
 *
 * Manages the preferred display units (TiB vs TB) for the calculator.
 * - TiB (Tebibyte) = 1024^4 bytes (binary, ZFS standard)
 * - TB (Terabyte) = 1000^4 bytes (decimal, manufacturer standard)
 */

import { useCurrentConfig } from './useScenarios';

export type DisplayUnit = 'TiB' | 'TB';

const STORAGE_KEY = 'zfs-calculator-display-units';

/**
 * Conversion factors
 * 1 TiB = 1024^4 bytes
 * 1 TB = 1000^4 bytes
 * 1 TB ≈ 0.9095 TiB
 */
export const CONVERSION_FACTORS = {
  TB_TO_TIB: 1000^4 / 1024^4, // ~0.9095
  TIB_TO_TB: 1024^4 / 1000^4, // ~1.0995
};

/**
 * Convert TiB to TB
 */
export function tibToTB(tib: number): number {
  return tib * CONVERSION_FACTORS.TIB_TO_TB;
}

/**
 * Convert TB to TiB
 */
export function tbToTiB(tb: number): number {
  return tb * CONVERSION_FACTORS.TB_TO_TIB;
}

/**
 * Format capacity with appropriate units
 */
export function formatCapacity(
  valueTiB: number,
  unit: DisplayUnit,
  decimals: number = 2
): string {
  const value = unit === 'TB' ? tibToTB(valueTiB) : valueTiB;
  return `${value.toFixed(decimals)} ${unit}`;
}

/**
 * Hook for managing display units
 */
export function useDisplayUnits() {
  const [unit, setUnitState] = useCurrentConfig<DisplayUnit>('TiB', STORAGE_KEY);

  const setUnit = (newUnit: DisplayUnit) => {
    setUnitState(newUnit);
  };

  const toggleUnit = () => {
    setUnit(unit === 'TiB' ? 'TB' : 'TiB');
  };

  return {
    unit,
    setUnit,
    toggleUnit,
    isTiB: unit === 'TiB',
    isTB: unit === 'TB',
    formatCapacity: (valueTiB: number, decimals?: number) =>
      formatCapacity(valueTiB, unit, decimals),
    convertToDisplay: (valueTiB: number) =>
      unit === 'TB' ? tibToTB(valueTiB) : valueTiB,
    convertFromDisplay: (displayValue: number) =>
      unit === 'TB' ? tbToTiB(displayValue) : displayValue,
  };
}
